import type { VotingLocation } from 'admin/src/types/VotingLocation';
import type { CellStatus } from 'admin/src/screens/admin/Pollworker/StaffingChart/enums';

export interface StaffingRole {
  id: string;
  roleName: string;
  active?: boolean;
}

export interface PartyCount {
  partyCode: string;
  partyId: string;
  partyName?: string;
  count: number;
  color: string;
}

export interface StaffingCellData {
  total: number;
  partyBreakdown?: PartyCount[];
  isNotApplicable?: boolean;
}

export interface WorkerAssignment {
  oderId: string;
  evUserId: string;
  workerName: string;
  partyCode?: string;
  assignedAt: string;
  startTime?: string;
  endTime?: string;
  userConfirmationStatus?: number;
  isReleased?: boolean;
  isLegacySchedule?: boolean;
}

export interface StaffingCellSchedule extends StaffingCellData {
  assignments: WorkerAssignment[];
}

export interface StaffingRoleData {
  roleId: string;
  role: StaffingRole;
  instanceIndex: number;
  cells: Record<string, StaffingCellData>;
  isRogue?: boolean;
  isAdded?: boolean;
}

export interface StaffingLocationData {
  locationId: string;
  location: VotingLocation;
  roles: StaffingRoleData[];
}

export interface StaffingChartProps {
  locations: StaffingLocationData[];
  dates: string[];
  electionDate: string;
  requiresPartyBalance: boolean;
  availableRoles: StaffingRole[];
  parties: PartyConfig[];
  onCellUpdate: (locationId: string, roleId: string, instanceIndex: number, date: string, data: StaffingCellData) => void;
  onAddRole: (locationId: string, roleId: string) => void;
  onDuplicateRole: (locationId: string, roleId: string, instanceIndex: number) => void;
  onCopyRoleToLocations: (sourceLocationId: string, roleId: string, instanceIndex: number, targetLocationIds: string[]) => void;
  onRemoveRole: (locationId: string, roleId: string, instanceIndex: number) => void;
}

export interface PartyConfig {
  id: string;
  partyCode: string;
  partyName: string;
  color: string;
}

export const PARTY_COLORS: Record<string, string> = {
  D: '#3b82f6',
  R: '#ef4444',
  I: '#eab308',
};

export const PARTY_LABELS: Record<string, string> = {
  D: 'Democrat',
  R: 'Republican',
  I: 'Independent',
  L: 'Libertarian',
  G: 'Green',
  N: 'Non-Partisan',
  E: 'Other',
  S: 'Socialist',
};

export interface SlotAssignment {
  slotNumber: number;
  date: string;
  partyRequirement: {
    partyId: string;
    partyName: string;
  } | null;
  assignedWorker: {
    evUserId: string;
    firstName: string;
    lastName: string;
    party: string;
  } | null;
}

export interface ExtraAssignment {
  scheduleId: string;
  evUserId: string;
  firstName: string;
  lastName: string;
  party: string;
  reason: string;
}

export type CellStaffingStatus = CellStatus;

export interface CellStatusMetadata {
  status: CellStaffingStatus;
  required: number;
  assigned: number;
  overBy: number;
  partyMet: boolean;
  partyNeeds: { code: string; needed: number; have: number }[] | null;
}

// Search types for multi-entity fuzzy search
export type SearchableItem =
  | { type: 'location'; id: string; locationId: string; name: string }
  | { type: 'role'; id: string; locationId: string; roleId: string; locationName: string; roleName: string }
  | { type: 'worker'; id: string; evUserId: string; firstName: string; lastName: string; fullName: string };
